import type { InstantSearchStatus } from 'instantsearch.js';

import { INSTANT_SEARCH_STATUS } from '@/lib/const';

/**
 * Fields that are numeric and should not be quoted in Algolia filters
 */
const NUMERIC_FIELDS = [
  'size',
  'price',
  'strap_length',
  'distance_between_horns',
];

/**
 * Checks if a field value should be treated as numeric
 */
function isNumericField(field: string): boolean {
  return NUMERIC_FIELDS.includes(field);
}

/**
 * Locale-specific fields that need an English suffix appended.
 * The suffix (EN_US or EN_GB) is determined by the country's English locale.
 * Fields without locale versions are used as-is (e.g., size, collection).
 */
const LOCALE_SPECIFIC_FIELDS = [
  'case',
  'dial_color',
  'type',
  'lens_color',
  'frame_color',
  'frame_shape',
  'frame_material',
  'distance_between_horns',
];

/**
 * Allowlist of URL query parameter names that are valid Algolia filter fields.
 * Only these params will be converted into Algolia filters.
 * Unknown params (e.g. tracking params like srsltid, gclid, fbclid) are ignored
 * so they don't produce zero-result queries.
 */
const VALID_FILTER_PARAMS = new Set([
  ...LOCALE_SPECIFIC_FIELDS,
  'size',
  'collection',
  'material',
  'color',
  'strap_length',
  'buckle_type',
  'lug_type',
  'objectID',
]);

/**
 * Parses URL query parameters and builds Algolia filters using the appropriate English
 * locale field. The englishLocaleCode (e.g. 'EN_US' or 'EN_GB') must match the English
 * attribute name used by the target Algolia index (e.g. us-usd uses EN_US, pt-eur uses EN_GB).
 */
export const parseUrlFiltersToEnFields = (
  url: string,
  englishLocaleCode = 'EN_US',
): string => {
  const params = new URLSearchParams(url.split('?')[1]);
  const filters: string[] = [];
  const fieldValues: Record<string, string[]> = {};

  const fieldToEnMap: Record<string, string> = Object.fromEntries(
    LOCALE_SPECIFIC_FIELDS.map((field) => [
      field,
      `${field}.${englishLocaleCode}`,
    ]),
  );

  // Collect all values for each field
  params.forEach((value, key) => {
    // Only process params that are known Algolia filter fields
    if (!VALID_FILTER_PARAMS.has(key)) {
      return;
    }

    // Trim the value but keep the full v2 format (e.g., "Stainless steel | STSTL")
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return;
    }

    // Map to English locale field if applicable, otherwise use original field name
    const filterField = fieldToEnMap[key] || key;

    if (!fieldValues[filterField]) {
      fieldValues[filterField] = [];
    }
    fieldValues[filterField].push(trimmedValue);
  });

  // Build filters from collected values
  Object.entries(fieldValues).forEach(([field, values]) => {
    const baseField = field.split('.')[0]; // Extract base field name (e.g., "case" from "case.EN_US")
    const isNumeric = isNumericField(baseField);

    if (values.length === 1) {
      // For numeric fields, don't quote the value
      if (isNumeric) {
        filters.push(`(${field}:${values[0]})`);
      } else {
        filters.push(`(${field}:"${values[0]}")`);
      }
    } else {
      // Multiple values - use OR
      if (isNumeric) {
        filters.push(
          `(${values.map((val) => `${field}:${val}`).join(' OR ')})`,
        );
      } else {
        filters.push(
          `(${values.map((val) => `${field}:"${val}"`).join(' OR ')})`,
        );
      }
    }
  });

  // Handle price range if present
  const priceFrom = params.get('priceFrom');
  const priceTo = params.get('priceTo');
  if (priceFrom && !isNaN(Number(priceFrom))) {
    filters.push(`(price >= ${priceFrom})`);
  }
  if (priceTo && !isNaN(Number(priceTo))) {
    filters.push(`(price <= ${priceTo})`);
  }

  return filters.length > 0 ? filters.join(' AND ') : '';
};

export const parseUiStateFilters = (initialUiState: any): string => {
  const filters: string[] = [];

  if (initialUiState.refinementList) {
    Object.entries(initialUiState.refinementList).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        filters.push(
          `(${value.map((val) => key + ':"' + val + '"').join(' OR ')})`,
        );
      } else if (value && !Array.isArray(value)) {
        filters.push(`(${key}:"${value}")`);
      }
    });

    if (initialUiState?.range?.price) {
      const [priceFrom, priceTo] = initialUiState.range.price.split(':');
      priceFrom && filters.push(`(price >= ${priceFrom})`);
      priceTo && filters.push(`(price <= ${priceTo})`);
    }
  }

  return filters.length > 0 ? filters.join(' AND ') : '';
};

export function isInstantSearchLoading(status: InstantSearchStatus): boolean {
  return (
    status === INSTANT_SEARCH_STATUS.Loading ||
    status === INSTANT_SEARCH_STATUS.Stalled
  );
}
